import { Component, computed, inject, input, output, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import type { CustomFieldRawValue, CustomFieldViewModel } from '../../models/custom-field.model';
import {
  clampRatingLevels,
  normalizeRatingValue,
  starVisualState,
  type StarVisualState,
} from '../../utils/rating-field.util';

@Component({
  selector: 'app-cf-rating-field',
  standalone: true,
  imports: [LucideAngularModule],
  template: `
    <div class="cf-rating" [class.cf-rating--compact]="compact()" [class.cf-rating--readonly]="isReadonly()">
      @if (showLabel()) {
        <div class="cf-rating__label-row">
          <span class="cf-rating__label">{{ field().name }}</span>
          @if (field().required) {
            <span class="cf-rating__req" aria-hidden="true">*</span>
          }
        </div>
      }

      <div
        class="cf-rating__row"
        [attr.role]="isReadonly() ? 'img' : 'group'"
        [attr.aria-label]="groupAriaLabel()"
        (mouseleave)="onRowLeave()"
      >
        @for (i of starIndexes(); track i) {
          <div
            class="cf-rating__star"
            [class.cf-rating__star--interactive]="!isReadonly()"
            (mousemove)="onStarMove(i, $event)"
          >
            <div class="cf-rating__star-stack" aria-hidden="true">
              <svg class="cf-rating__icon cf-rating__icon--empty" viewBox="0 0 24 24" aria-hidden="true">
                <defs>
                  <linearGradient [attr.id]="svgGradId('e')" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stop-color="#E5E7EB"></stop>
                    <stop offset="100%" stop-color="#CBD5E1"></stop>
                  </linearGradient>
                </defs>
                <path
                  class="cf-rating__path-empty"
                  [attr.fill]="gradUrl('e')"
                  d="M12 2.5l2.93 5.94 6.56.95-4.74 4.62 1.12 6.53L12 17.47l-5.87 3.07 1.12-6.53L2.5 9.39l6.56-.95L12 2.5z"
                ></path>
              </svg>
              <span class="cf-rating__fill-clip" [style.width.%]="fillPercentForStar(i)">
                <svg class="cf-rating__icon cf-rating__icon--fill" viewBox="0 0 24 24" aria-hidden="true">
                  <defs>
                    <linearGradient [attr.id]="svgGradId('fill')" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#FFF6A3"></stop>
                      <stop offset="58%" stop-color="#FFDE21"></stop>
                      <stop offset="100%" stop-color="#E2B20A"></stop>
                    </linearGradient>
                    <linearGradient [attr.id]="svgGradId('hi')" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0.6"></stop>
                      <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"></stop>
                    </linearGradient>
                    <linearGradient [attr.id]="svgGradId('sh')" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stop-color="#000000" stop-opacity="0"></stop>
                      <stop offset="100%" stop-color="#000000" stop-opacity="0.14"></stop>
                    </linearGradient>
                  </defs>
                  <path
                    class="cf-rating__fill-shape"
                    [attr.fill]="gradUrl('fill')"
                    d="M12 2.5l2.93 5.94 6.56.95-4.74 4.62 1.12 6.53L12 17.47l-5.87 3.07 1.12-6.53L2.5 9.39l6.56-.95L12 2.5z"
                  ></path>
                  <path
                    class="cf-rating__fill-highlight"
                    [attr.fill]="gradUrl('hi')"
                    d="M12 2.5l2.93 5.94 6.56.95-4.74 4.62 1.12 6.53L12 17.47l-5.87 3.07 1.12-6.53L2.5 9.39l6.56-.95L12 2.5z"
                  ></path>
                  <path
                    class="cf-rating__fill-shade"
                    [attr.fill]="gradUrl('sh')"
                    d="M12 2.5l2.93 5.94 6.56.95-4.74 4.62 1.12 6.53L12 17.47l-5.87 3.07 1.12-6.53L2.5 9.39l6.56-.95L12 2.5z"
                  ></path>
                </svg>
              </span>
            </div>
            @if (!isReadonly()) {
              <button
                type="button"
                class="cf-rating__hit cf-rating__hit--left"
                [disabled]="disabled()"
                [attr.aria-label]="starAriaLabel(i, 'half')"
                (click)="onHalfClick(i, $event)"
              ></button>
              <button
                type="button"
                class="cf-rating__hit cf-rating__hit--right"
                [disabled]="disabled()"
                [attr.aria-label]="starAriaLabel(i, 'full')"
                (click)="onFullClick(i, $event)"
              ></button>
            }
          </div>
        }

        @if (compact()) {
          <span class="cf-rating__text" [attr.aria-hidden]="true">{{ compactText() }}</span>
        }

        @if (!isReadonly() && !field().required && parsedValue() != null) {
          @if (clearPresentation() === 'icon') {
            <button
              type="button"
              class="cf-rating__clear-icon"
              [disabled]="disabled()"
              [attr.aria-label]="t('customField.rating.clear', undefined, 'Limpar')"
              [attr.title]="t('customField.rating.clear', undefined, 'Limpar')"
              (click)="clear($event)"
            >
              <lucide-icon name="x" class="h-3.5 w-3.5"></lucide-icon>
            </button>
          } @else {
            <button
              type="button"
              class="cf-rating__clear"
              [disabled]="disabled()"
              (click)="clear($event)"
            >
              {{ t('customField.rating.clear', undefined, 'Limpar') }}
            </button>
          }
        }
      </div>
    </div>
  `,
  styles: [
    `
      .cf-rating {
        display: grid;
        gap: 6px;
        min-width: 0;
      }

      .cf-rating__label-row {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .cf-rating__label {
        font-size: 13px;
        font-weight: 600;
        color: var(--text);
      }

      .cf-rating__req {
        color: var(--danger);
        font-size: 12px;
      }

      .cf-rating__row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 6px;
      }

      .cf-rating--compact .cf-rating__row {
        gap: 2px 4px;
      }

      .cf-rating__star {
        position: relative;
        width: 24px;
        height: 24px;
        flex-shrink: 0;
      }

      .cf-rating--compact .cf-rating__star {
        width: 15px;
        height: 15px;
      }

      .cf-rating__star-stack {
        position: absolute;
        inset: 0;
        pointer-events: none;
      }

      .cf-rating__icon {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }

      .cf-rating__path-empty {
        stroke: color-mix(in srgb, #64748b 70%, var(--border));
        stroke-width: 1.4px;
      }

      .cf-rating__icon--fill {
        overflow: visible;
      }

      .cf-rating__fill-shape {
        stroke: #b48a00;
        stroke-width: 1px;
      }

      .cf-rating__fill-highlight {
        clip-path: inset(0 0 56% 0);
        pointer-events: none;
      }

      .cf-rating__fill-shade {
        pointer-events: none;
      }

      .cf-rating__fill-clip {
        position: absolute;
        inset: 0 auto 0 0;
        overflow: hidden;
        transition: width 130ms ease-out;
      }

      .cf-rating__hit {
        position: absolute;
        top: 0;
        bottom: 0;
        width: 50%;
        padding: 0;
        margin: 0;
        border: 0;
        background: transparent;
        cursor: pointer;
        z-index: 1;
      }

      .cf-rating__hit:disabled {
        cursor: not-allowed;
        opacity: 0.45;
      }

      .cf-rating__hit--left {
        left: 0;
      }

      .cf-rating__hit--right {
        right: 0;
      }

      .cf-rating--readonly .cf-rating__star--interactive {
        cursor: default;
      }

      .cf-rating__star--interactive:hover .cf-rating__fill-shape {
        filter: brightness(1.07) saturate(1.06);
      }

      .cf-rating__fill-shape,
      .cf-rating__fill-highlight,
      .cf-rating__fill-shade {
        transition: filter 130ms ease-out, opacity 130ms ease-out;
      }

      .cf-rating__text {
        font-size: 11px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: var(--muted);
        margin-left: 4px;
      }

      .cf-rating__clear {
        margin-left: 8px;
        font-size: 11px;
        font-weight: 600;
        color: var(--muted);
        background: transparent;
        border: 0;
        padding: 2px 6px;
        border-radius: 6px;
        cursor: pointer;
      }

      .cf-rating__clear:hover:not(:disabled) {
        color: var(--text);
        background: color-mix(in srgb, var(--hover) 80%, transparent);
      }

      .cf-rating__clear:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .cf-rating__clear-icon {
        margin-left: 2px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        flex-shrink: 0;
        border-radius: 8px;
        border: 1px solid transparent;
        background: transparent;
        color: var(--muted);
        cursor: pointer;
      }

      .cf-rating__clear-icon:hover:not(:disabled) {
        color: var(--text);
        background: color-mix(in srgb, var(--hover) 75%, transparent);
        border-color: var(--border);
      }

      .cf-rating__clear-icon:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    `,
  ],
})
export class CfRatingFieldComponent {
  private readonly i18n = inject(I18nService);
  readonly t = this.i18n.t.bind(this.i18n);

  /** Sufixo único por instância (lista com N linhas = N avaliações sem colidir `url(#id)`). */
  private readonly gradUid = `r${Math.random().toString(36).slice(2, 11)}`;

  svgGradId(suffix: string): string {
    return `${this.gradUid}-${suffix}`;
  }

  gradUrl(suffix: string): string {
    return `url(#${this.svgGradId(suffix)})`;
  }

  readonly field = input.required<CustomFieldViewModel>();
  readonly value = input<CustomFieldRawValue>(null);
  readonly showLabel = input<boolean>(true);
  /**
   * `text` — link "Limpar" (padrão no card).
   * `icon` — botão só com X (lista / UI compacta).
   */
  readonly clearPresentation = input<'text' | 'icon'>('text');
  /** Lista/board: estrelas menores + texto "x/y". */
  readonly compact = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly valueChanged = output<CustomFieldRawValue>();

  /** Preview durante hover (somente modo interativo). */
  private readonly hoverPreview = signal<number | null>(null);

  readonly maxLevels = computed(() => clampRatingLevels(this.field().ratingLevels));

  readonly starIndexes = computed(() =>
    Array.from({ length: this.maxLevels() }, (_, k) => k + 1)
  );

  readonly parsedValue = computed(() => normalizeRatingValue(this.value(), this.maxLevels()));

  /** `compact` só reduz o tamanho visual; a lista continua editável. */
  readonly isReadonly = computed(() => this.field().readOnly || this.disabled());

  readonly groupAriaLabel = computed(() => {
    const max = this.maxLevels();
    const v = this.parsedValue();
    if (v == null) {
      return this.t('customField.rating.ariaEmpty', { max }, `Sem avaliação; escala até ${max}`);
    }
    const formatted = Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ',');
    return this.t(
      'customField.rating.ariaValue',
      { value: formatted, max },
      `${formatted} de ${max}`
    );
  });

  readonly compactText = computed(() => {
    const max = this.maxLevels();
    const v = this.parsedValue();
    if (v == null) return `—/${max}`;
    const dec = Number.isInteger(v) ? String(v) : v.toFixed(1).replace('.', ',');
    return `${dec}/${max}`;
  });

  displayStateForStar(starIndex1Based: number): StarVisualState {
    const preview = this.hoverPreview();
    const v = preview != null ? preview : this.parsedValue();
    return starVisualState(starIndex1Based, v);
  }

  fillPercentForStar(starIndex1Based: number): number {
    const state = this.displayStateForStar(starIndex1Based);
    if (state === 'full') return 100;
    if (state === 'half') return 50;
    return 0;
  }

  starAriaLabel(starIndex1Based: number, kind: 'half' | 'full'): string {
    const max = this.maxLevels();
    const val = kind === 'half' ? starIndex1Based - 0.5 : starIndex1Based;
    const formatted = Number.isInteger(val) ? String(val) : val.toFixed(1).replace('.', ',');
    return this.t(
      'customField.rating.ariaStar',
      { value: formatted, max },
      `${formatted} de ${max}`
    );
  }

  onStarMove(starIndex1Based: number, event: MouseEvent): void {
    if (this.isReadonly()) return;
    const el = event.currentTarget as HTMLElement | null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const left = x < rect.width / 2;
    const v = left ? starIndex1Based - 0.5 : starIndex1Based;
    this.hoverPreview.set(this.clampStep(v));
  }

  onRowLeave(): void {
    this.hoverPreview.set(null);
  }

  onHalfClick(starIndex1Based: number, event: MouseEvent): void {
    event.preventDefault();
    this.applyClick(starIndex1Based - 0.5);
  }

  onFullClick(starIndex1Based: number, event: MouseEvent): void {
    event.preventDefault();
    this.applyClick(starIndex1Based);
  }

  private applyClick(rawTarget: number): void {
    if (this.isReadonly()) return;
    const max = this.maxLevels();
    const target = this.clampStep(rawTarget);
    const cur = this.parsedValue();
    if (cur != null && Math.abs(cur - target) < 0.001 && !this.field().required) {
      this.valueChanged.emit(null);
      this.hoverPreview.set(null);
      return;
    }
    this.valueChanged.emit(target);
    this.hoverPreview.set(null);
  }

  clear(event: Event): void {
    event.preventDefault();
    if (this.field().required || this.isReadonly()) return;
    this.valueChanged.emit(null);
    this.hoverPreview.set(null);
  }

  private clampStep(v: number): number {
    const max = this.maxLevels();
    const step = 0.5;
    const snapped = Math.round(v / step) * step;
    return Math.min(max, Math.max(step, snapped));
  }
}
